import { Briefcase, Check, CheckCircle2, Heart, MapPin, Shield } from "lucide-react";

import { photoUri } from "@/lib/api";
import { MISSION_INFO } from "@/lib/constants";
import { Button, Pill } from "@/components/ui";

/** Photo-forward member card used in Discover, Today's Top Matches and Saved. */
export function MatchCard({ profile: p, requestLabel, onView, onRequest, wide, index = 0, preview, onToggleSave, testPrefix }) {
  const tp = testPrefix ? `${testPrefix}-` : "";
  const verified = p.verification_status === "verified";
  const mission = p.mission ? MISSION_INFO[p.mission] : null;
  return (
    <article data-testid={`${tp}match-card-${p.id}`} style={{ animationDelay: `${Math.min(index, 10) * 60}ms` }}
      className="fade-up group flex flex-col overflow-hidden rounded-[20px] border border-border bg-white shadow-sm transition-shadow duration-300 hover:shadow-lg">
      <div className={`relative overflow-hidden bg-[#F4EBE6] ${wide ? "aspect-[4/4.4]" : "aspect-[3/4]"}`}>
        <button type="button" onClick={onView} disabled={!onView} data-testid={`${tp}match-card-photo-${p.id}`} aria-label={`View ${p.display_name}`} className="absolute inset-0 h-full w-full">
          <img src={photoUri(p.main_photo_url)} alt={p.display_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        </button>
        <div className="pointer-events-none absolute inset-x-2.5 top-2.5 flex items-start justify-between">
          {p.is_test_profile ? <Pill label="Test profile" testId={`${tp}match-test-badge-${p.id}`} /> : <span />}
          {typeof p.compatibility === "number" ? (
            <span data-testid={`${tp}match-compat-${p.id}`} className="rounded-full bg-white/95 px-2.5 py-1 text-xs font-bold text-primary shadow-sm">{p.compatibility}%</span>
          ) : null}
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent to-[rgba(42,39,39,0.82)] px-3 pb-3 pt-10">
          <h3 data-testid={`${tp}match-name-${p.id}`} className={`truncate text-[#FDFBF7] ${wide ? "text-[28px]" : "text-[22px]"} leading-tight`}>{p.display_name}, {p.age}</h3>
          <div className="mt-0.5 flex items-center gap-1">
            {verified ? <CheckCircle2 className="h-3 w-3 text-[#C19B53]" /> : <Shield className="h-3 w-3 text-[#FDFBF7]" />}
            <span data-testid={`${tp}match-verification-${p.id}`} className={`text-[11px] ${verified ? "font-bold text-[#C19B53]" : "text-[#FDFBF7]"}`}>{verified ? "Verified Member" : "Unverified"}</span>
            {p.online ? <span data-testid={`${tp}match-online-${p.id}`} className="ml-1 h-2 w-2 rounded-full bg-[#3A6C54] ring-2 ring-white" /> : null}
          </div>
        </div>
        {onToggleSave ? (
          <button type="button" data-testid={`${tp}match-save-${p.id}`} aria-label={p.saved ? "Remove from saved" : "Save profile"} onClick={onToggleSave}
            className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-primary shadow transition-transform hover:scale-110">
            <Heart className={`h-[18px] w-[18px] ${p.saved ? "fill-primary" : ""}`} />
          </button>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5 shrink-0" />{p.city}, {p.country}</p>
        {p.occupation ? <p className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Briefcase className="h-3.5 w-3.5 shrink-0" />{p.occupation}</p> : null}
        {mission ? <div><Pill label={mission.label} tone="rose" icon={mission.Icon} testId={`${tp}match-mission-${p.id}`} /></div> : null}
        {p.bio ? <p className={`text-[13px] leading-[18px] ${wide ? "line-clamp-3" : "line-clamp-2"}`}>{p.bio}</p> : null}
        {p.shared_interests?.length ? <p className="truncate text-xs text-primary"><b>Shared: </b>{p.shared_interests.join(", ")}</p> : null}
        {!preview ? (
          <div className={`mt-auto flex gap-2 pt-2 ${wide ? "flex-row" : "flex-col"}`}>
            <Button testId={`${tp}match-view-profile-${p.id}`} size="sm" variant="outline" title="View Profile" onClick={onView} className={wide ? "flex-1" : "w-full"} />
            {p.request_pending ? (
              <Button testId={`${tp}match-request-sent-${p.id}`} size="sm" variant="rose" title="Request sent" icon={Check} disabled className={`${wide ? "flex-1" : "w-full"} !opacity-100`} />
            ) : (
              <Button testId={`${tp}match-request-btn-${p.id}`} size="sm" title={requestLabel ?? "Request"} onClick={onRequest} className={wide ? "flex-1" : "w-full"} />
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}
